import { useState } from "react";
import { Button, Card, Checkbox, Group, Stack, Text } from "@/components/ui";
import type { OrganizacionAdmin } from "@/lib/types";
import { useCambiarOrganizaciones } from "../hooks/use-usuarios-mutations";

/**
 * Editor de organizaciones permitidas. Una orga inactiva solo es
 * checkable si ya estaba marcada (preserva histórico · decisión 65).
 * El label muestra el número SAP en tipografía monospace para que el
 * admin pueda verificar.
 */
export function SeccionOrganizaciones({
  usuarioId,
  organizacionesActuales,
  todasOrganizaciones,
}: {
  usuarioId: string;
  organizacionesActuales: { id: string }[];
  todasOrganizaciones: OrganizacionAdmin[];
}) {
  const [sel, setSel] = useState<Set<string>>(new Set(organizacionesActuales.map((o) => o.id)));
  const guardar = useCambiarOrganizaciones();

  const toggle = (id: string) => {
    const next = new Set(sel);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSel(next);
  };

  return (
    <Card padding="md">
      <Stack gap="sm">
        <div>
          <Text size="xs" fw={600} className="uppercase tracking-wide" c="dimmed">
            Organizaciones permitidas
          </Text>
          <Text size="xs" c="dimmed" className="mt-0.5">
            Solo aplica a Solicitante / Autorizador. Vacío = sin permisos (Auditor y Administrador ven todas implícitamente).
          </Text>
        </div>
        {todasOrganizaciones.length === 0 ? (
          <Text size="xs" c="dimmed">
            No hay organizaciones.
          </Text>
        ) : (
          <div className="h-45 overflow-y-auto pr-1">
            <Stack gap={6}>
              {todasOrganizaciones.map((o) => (
                <Checkbox
                  key={o.id}
                  checked={sel.has(o.id)}
                  disabled={!o.estaActiva && !sel.has(o.id)}
                  onCheckedChange={() => toggle(o.id)}
                  label={
                    <Group gap={6} className="flex-nowrap">
                      <Text size="sm" className={!o.estaActiva ? "text-muted-foreground line-through" : undefined}>
                        {o.nombre}
                      </Text>
                      <Text size="xs" c="dimmed" className="font-mono">
                        {o.numeroSociedad}
                      </Text>
                    </Group>
                  }
                />
              ))}
            </Stack>
          </div>
        )}
        <Button
          size="xs"
          fullWidth
          loading={guardar.isPending}
          onClick={() => guardar.mutate({ usuarioId, organizacionIds: Array.from(sel) })}
        >
          Guardar organizaciones
        </Button>
      </Stack>
    </Card>
  );
}
